/**
 * DonationsPage — staff view of gifts, with a Receipts working surface.
 *
 * Phases 1 and 5. Previously this was an unfiltered list where the only hint of a
 * receipt was tiny grey text, event-tagged gifts were indistinguishable from any
 * other (the API never returned `event_id`), and there was no way to re-open,
 * re-send, or chase a receipt.
 *
 * Two views:
 *   Gifts    — filter by event, method, campaign, year, receipt state, free text.
 *   Receipts — the working queue: un-receipted, issued-but-not-sent, failed, and
 *              the gifts that cannot be receipted because no donor email was ever
 *              captured (which used to be a silent no-op).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  DollarSign,
  Download,
  FileText,
  Heart,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTabbedChrome } from '@/components/weavein';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Donation,
  DonationFilters,
  DonationMethod,
  ReceiptQueue,
  ReceiptState,
  bulkIssueReceipts,
  createOfflineDonation,
  getReceiptQueue,
  issueReceipt,
  listDonations,
  openReceiptPdf,
} from '@/services/api/donations';
import { DonorEvent, listDonorEvents } from '@/services/api/donorEvents';
import OfflineGiftForm from '@/components/donors/OfflineGiftForm';

type View = 'gifts' | 'receipts' | 'new';

function fmtCurrency(n?: number | null, currency = 'USD'): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(n));
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  succeeded: 'secondary',
  pending: 'default',
  failed: 'destructive',
  refunded: 'outline',
  partially_refunded: 'outline',
};

/** Receipt state as an explicit chip — a stamped number is not proof of delivery. */
function ReceiptChip({ d }: { d: Donation }) {
  const state: ReceiptState = d.receipt_state || (d.receipt_sent_at ? 'sent' : d.receipt_number ? 'issued' : 'none');
  const map: Record<ReceiptState, { label: string; cls: string; title?: string }> = {
    sent: {
      label: `Receipt sent${d.receipt_channel ? ` · ${d.receipt_channel}` : ''}`,
      cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      title: d.receipt_sent_at ? new Date(d.receipt_sent_at).toLocaleString() : undefined,
    },
    issued: {
      label: 'Issued, not sent',
      cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      title: d.receipt_last_error || 'The receipt exists but has not reached the donor.',
    },
    failed: {
      label: 'Send failed',
      cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      title: d.receipt_last_error || undefined,
    },
    none: { label: 'No receipt', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300' },
  };
  const s = map[state];
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] ${s.cls}`} title={s.title}>
      {s.label}
    </span>
  );
}

function GiftRow({ d, onChanged }: { d: Donation; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const needsEmail = !d.donor_email;

  const send = async () => {
    setBusy('send');
    setMsg(null);
    try {
      const r = await issueReceipt(d.id, { to_email: email.trim() || undefined });
      setMsg(r.sent ? 'Receipt sent.' : r.reason === 'no_donor_email' ? 'Receipt issued — add an email to send it.' : r.error || 'Could not send.');
      onChanged();
    } catch (e: any) {
      setMsg(e?.message || 'Could not issue the receipt.');
    } finally {
      setBusy(null);
    }
  };

  const openPdf = async () => {
    setBusy('pdf');
    setMsg(null);
    try {
      await openReceiptPdf(d.id);
    } catch (e: any) {
      setMsg(e?.message || 'Could not open the receipt.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-gray-900 dark:text-gray-100">
              {d.donor_display_name || d.donor_name || 'Anonymous donor'}
            </span>
            <Badge variant={STATUS_VARIANT[d.status] || 'outline'}>{d.status}</Badge>
            <Badge variant="outline">{d.method}</Badge>
            {d.status === 'succeeded' ? <ReceiptChip d={d} /> : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
            {d.event_title ? (
              <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                <Calendar size={11} /> {d.event_title}
              </span>
            ) : null}
            {d.campaign ? <span>{d.campaign}</span> : null}
            <span>{new Date(d.created_at).toLocaleDateString()}</span>
            {d.receipt_number ? <span>· {d.receipt_number}</span> : null}
            {d.donor_email ? <span>· {d.donor_email}</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="font-semibold text-gray-900 dark:text-gray-100">{fmtCurrency(d.amount, d.currency)}</div>
            {Number(d.platform_fee) > 0 ? (
              <div className="text-xs text-gray-400">fee {fmtCurrency(d.platform_fee, d.currency)}</div>
            ) : null}
          </div>
          {d.status === 'succeeded' ? (
            <div className="flex items-center gap-1">
              <button
                onClick={openPdf}
                disabled={busy !== null}
                title="Open the receipt PDF"
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
              >
                {busy === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
              </button>
              <button
                onClick={send}
                disabled={busy !== null || (needsEmail && !email.trim())}
                title={d.receipt_sent_at ? 'Re-send the receipt' : 'Issue and send the receipt'}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 dark:hover:bg-gray-700"
              >
                {busy === 'send' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {d.status === 'succeeded' && needsEmail ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded bg-amber-50 px-2 py-1.5 dark:bg-amber-900/20">
          <AlertTriangle size={13} className="text-amber-600" />
          <span className="text-xs text-amber-800 dark:text-amber-300">No donor email — the receipt cannot be sent.</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Add an email to send it"
            className="flex-1 min-w-[12rem] rounded border border-amber-200 px-2 py-1 text-xs dark:border-amber-900 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
      ) : null}
      {msg ? <p className="mt-1 text-xs text-gray-500">{msg}</p> : null}
    </div>
  );
}

export const DonationsPage: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [view, setView] = useState<View>('gifts');
  const [rows, setRows] = useState<Donation[] | null>(null);
  const [queue, setQueue] = useState<ReceiptQueue | null>(null);
  const [events, setEvents] = useState<DonorEvent[]>([]);
  const [filters, setFilters] = useState<DonationFilters>({ limit: 200 });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadGifts = useCallback(async () => {
    setError(null);
    try {
      setRows(await listDonations(filters));
    } catch (e: any) {
      setError(e?.message || 'Failed to load donations.');
      setRows([]);
    }
  }, [filters]);

  const loadQueue = useCallback(async () => {
    try {
      setQueue(await getReceiptQueue());
    } catch (e: any) {
      setError(e?.message || 'Failed to load the receipt queue.');
    }
  }, []);

  useEffect(() => {
    if (view === 'gifts') loadGifts();
    if (view === 'receipts') loadQueue();
  }, [view, loadGifts, loadQueue]);

  useEffect(() => {
    listDonorEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  const succeeded = useMemo(() => (rows || []).filter((d) => d.status === 'succeeded'), [rows]);
  const totals = useMemo(
    () => ({
      raised: succeeded.reduce((s, d) => s + Number(d.amount || 0), 0),
      fees: succeeded.reduce((s, d) => s + Number(d.platform_fee || 0), 0),
      net: succeeded.reduce((s, d) => s + Number(d.net_amount ?? d.amount ?? 0), 0),
    }),
    [succeeded],
  );

  const setFilter = (patch: Partial<DonationFilters>) => setFilters((f) => ({ ...f, ...patch }));

  const runBulk = async () => {
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const r = await bulkIssueReceipts({ only_unreceipted: true });
      setBulkMsg(
        `${r.sent} receipt${r.sent === 1 ? '' : 's'} sent` +
          (r.issued_not_sent ? `, ${r.issued_not_sent} issued but undeliverable (no donor email)` : '') +
          (r.failed ? `, ${r.failed} failed` : '') +
          '.',
      );
      await loadQueue();
    } catch (e: any) {
      setBulkMsg(e?.message || 'Bulk issue failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  if (view === 'new') {
    return (
      <OfflineGiftForm
        events={events}
        saving={saving}
        onCancel={() => setView('gifts')}
        onSubmit={async (data) => {
          setSaving(true);
          setError(null);
          try {
            await createOfflineDonation(data);
            setView('gifts');
            await loadGifts();
          } catch (e: any) {
            setError(e?.message || 'Failed to record the gift.');
          } finally {
            setSaving(false);
          }
        }}
      />
    );
  }

  const select =
    'rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!tabbed && <Heart size={26} className="text-blue-600" />}
          <div>
            {!tabbed && <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Donations</h1>}
            <p className="text-sm text-gray-500">Gifts, receipts, and offline entry.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => (view === 'gifts' ? loadGifts() : loadQueue())}>
            <RefreshCw size={15} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setView('new')}>
            <Plus size={15} className="mr-1" /> Record a gift
          </Button>
        </div>
      </header>

      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {(['gifts', 'receipts'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium capitalize ${
              view === v
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {v === 'gifts' ? <DollarSign size={15} /> : <Mail size={15} />} {v}
            {v === 'receipts' && queue && queue.unreceipted > 0 ? (
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[11px] text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                {queue.unreceipted}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {view === 'gifts' ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" /> Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4 text-center md:grid-cols-4">
                <div>
                  <div className="text-lg font-semibold">{fmtCurrency(totals.raised)}</div>
                  <div className="text-xs text-gray-500">Raised</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">{succeeded.length}</div>
                  <div className="text-xs text-gray-500">Gifts</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">{fmtCurrency(totals.fees)}</div>
                  <div className="text-xs text-gray-500">Platform fees</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">{fmtCurrency(totals.net)}</div>
                  <div className="text-xs text-gray-500">Net to org</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <input
              value={filters.search || ''}
              onChange={(e) => setFilter({ search: e.target.value || undefined })}
              onKeyDown={(e) => e.key === 'Enter' && loadGifts()}
              placeholder="Search donor, email, receipt…"
              className={`${select} min-w-[12rem] flex-1`}
            />
            <select
              value={filters.event_id ?? ''}
              onChange={(e) => setFilter({ event_id: e.target.value ? Number(e.target.value) : undefined })}
              className={select}
            >
              <option value="">All events</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
            <select
              value={filters.method ?? ''}
              onChange={(e) => setFilter({ method: (e.target.value as DonationMethod) || undefined })}
              className={select}
            >
              <option value="">All methods</option>
              {['card', 'cash', 'check', 'offline', 'other'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={filters.receipt_status ?? ''}
              onChange={(e) => setFilter({ receipt_status: (e.target.value as ReceiptState) || undefined })}
              className={select}
            >
              <option value="">Any receipt state</option>
              <option value="none">No receipt</option>
              <option value="issued">Issued, not sent</option>
              <option value="sent">Sent</option>
              <option value="failed">Send failed</option>
            </select>
            <select
              value={filters.year ?? ''}
              onChange={(e) => setFilter({ year: e.target.value ? Number(e.target.value) : undefined })}
              className={select}
            >
              <option value="">All years</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={loadGifts}>
              Apply
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {filters.event_id ? events.find((e) => e.id === filters.event_id)?.title || 'Event gifts' : 'All gifts'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {rows === null ? (
                <div className="py-6 text-sm text-gray-500">Loading donations…</div>
              ) : rows.length === 0 ? (
                <div className="py-6 text-sm text-gray-500">No donations match these filters.</div>
              ) : (
                <div className="divide-y dark:divide-gray-700">
                  {rows.map((d) => (
                    <GiftRow key={d.id} d={d} onChanged={loadGifts} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: 'No receipt yet', value: queue?.unreceipted ?? 0, tone: 'text-amber-600' },
              { label: 'Issued, not sent', value: queue?.issued_not_sent ?? 0, tone: 'text-amber-600' },
              { label: 'Send failed', value: queue?.failed ?? 0, tone: 'text-red-600' },
              { label: 'Sent', value: queue?.sent ?? 0, tone: 'text-green-600' },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className={`text-2xl font-semibold ${s.tone}`}>{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <Button disabled={bulkBusy || !queue?.unreceipted} onClick={runBulk}>
              {bulkBusy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />}
              Issue all outstanding receipts
            </Button>
            {queue && queue.blocked_no_email > 0 ? (
              <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle size={14} />
                {queue.blocked_no_email} gift{queue.blocked_no_email === 1 ? '' : 's'} have no donor email — add one below
                to deliver the receipt.
              </span>
            ) : queue && queue.unreceipted === 0 ? (
              <span className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 size={14} /> Every settled gift has a receipt.
              </span>
            ) : null}
          </div>
          {bulkMsg ? <p className="text-sm text-gray-600 dark:text-gray-300">{bulkMsg}</p> : null}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Download className="h-4 w-4" /> Needs a receipt
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {queue === null ? (
                <div className="py-6 text-sm text-gray-500">Loading…</div>
              ) : queue.pending.length === 0 ? (
                <div className="py-6 text-sm text-gray-500">Nothing outstanding.</div>
              ) : (
                <div className="divide-y dark:divide-gray-700">
                  {queue.pending.map((d) => (
                    <GiftRow key={d.id} d={d} onChanged={loadQueue} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DonationsPage;
